/**
 * GeographyModal Component
 *
 * Info popup for Region/District map labels — Overview (AI-generated content
 * sections), For Sale, and Sales Data tabs, mirroring NeighborhoodModal's
 * visual language and tab structure but without neighborhood-specific
 * surface (structured amenities/HOA/unit-features, View on Map) or a
 * favorite button - "favoriting" a whole district/region has no clear
 * meaning here. It does have an alert bell ("subscribe to updates in this
 * district/region"), same visual treatment as NeighborhoodModal's. See
 * NeighborhoodModal.ts for the sibling component this was modeled on; kept
 * deliberately separate rather than generalizing that component by entity
 * type (see plan rationale in the project notes).
 */
import { formatNeighborhoodName, formatListingAddress } from '@slowmaui/shared-types';
import { delta, formatCount, formatPrice, formatPsf, formatRatio, formatPercent } from '../MarketStats/index.js';
import { injectStyles } from './styles.js';
import { SalesChart } from '../SalesChart/index.js';
import { MarketTrendsChart } from '../MarketTrendsChart/index.js';
import { ActivityTable } from '../ActivityTable/index.js';
import { Disclaimers } from '../PropertyModal/Disclaimers.js';
function geographyEntityLabel(entityType) {
    if (entityType === 'county')
        return 'County';
    if (entityType === 'region')
        return 'Region';
    return 'District';
}
function geographyScopePhrase(entityType) {
    if (entityType === 'county')
        return 'this county';
    if (entityType === 'region')
        return 'this region';
    return 'this district';
}
// Same content sections + order as NeighborhoodModal's CONTENT_SECTIONS —
// both read from the same `content` table shape.
const CONTENT_SECTIONS = [
    { id: 'overview', name: 'Overview', icon: '📋' },
    { id: 'insights', name: 'Local Insights', icon: '💡' },
    { id: 'market', name: 'Market Analysis', icon: '📊' },
    { id: 'around', name: 'What\'s Nearby', icon: '📍' },
    { id: 'distances', name: 'Getting Around', icon: '🚗' },
    { id: 'similar', name: 'Similar Communities', icon: '🏘️' },
    { id: 'resources', name: 'Resources', icon: '📚' },
];
export class GeographyModal {
    options;
    data = null;
    entity = null;
    listings = [];
    salesData = null;
    salesChart = null;
    salesDisclaimers = null;
    marketData = null;
    marketChart = null;
    marketHeadline = null;
    activityTable = null;
    overlay = null;
    disclaimers = null;
    currentTab = 'overview';
    // Content tab (Agent Insight) state - see GeographyModalOptions'
    // onLoadAgentNote/onSaveAgentNote.
    agentNoteId = null;
    agentNoteText = '';
    agentNoteStatus = null;
    agentNoteAdminNote = null;
    agentNoteLoaded = false;
    expandedContentSections = new Set(['overview']);
    entityMap = null;
    entityMapPolygon = null;
    constructor(options) {
        this.options = options;
        injectStyles();
    }
    async open(data) {
        this.data = data;
        this.currentTab = 'overview';
        this.listings = [];
        this.salesData = null;
        this.marketData = null;
        this.marketHeadline = null;
        this.entity = null;
        this.activityTable?.destroy();
        this.activityTable = null;
        this.expandedContentSections = new Set(['overview']);
        this.agentNoteId = null;
        this.agentNoteText = '';
        this.agentNoteStatus = null;
        this.agentNoteAdminNote = null;
        this.agentNoteLoaded = false;
        this.render();
        this.show();
        if (this.options.onLoadEntityData) {
            this.loadEntityData(data);
        }
    }
    async loadEntityData(data) {
        if (!this.options.onLoadEntityData)
            return;
        try {
            const entity = await this.options.onLoadEntityData(data);
            if (entity) {
                this.entity = entity;
                this.updateWithEntityData();
            }
        }
        catch (error) {
            console.error('Failed to load geography entity data:', error);
        }
        finally {
            this.finalizeStatsLoadingState();
        }
    }
    /**
     * buildStatsRow()'s counts only ever come from the data passed
     * synchronously to open() - nothing re-renders #sm-geography-stats after
     * that. If that data was missing both counts (a caller gap, or this
     * entity load itself failing), the row's loading spinner would otherwise
     * spin forever with nothing left to resolve it. Once entity loading has
     * settled either way, clear a still-stuck spinner instead of leaving it.
     */
    finalizeStatsLoadingState() {
        const statsEl = this.overlay?.querySelector('#sm-geography-stats');
        if (statsEl?.querySelector('.sm-geography-loading')) {
            statsEl.innerHTML = '';
        }
    }
    async loadListings(data) {
        if (!this.options.onLoadListings)
            return;
        try {
            this.listings = await this.options.onLoadListings(data);
            this.updateListingsTab();
        }
        catch (error) {
            console.error('Failed to load listings:', error);
        }
    }
    async loadSalesData(data) {
        if (!this.options.onLoadSales)
            return;
        try {
            this.salesData = await this.options.onLoadSales(data);
            this.updateSalesTab();
        }
        catch (error) {
            console.error('Failed to load sales data:', error);
        }
    }
    async loadMarketData(data) {
        const { onLoadMarketData, onLoadMarketHeadline } = this.options;
        if (!onLoadMarketData && !onLoadMarketHeadline)
            return;
        try {
            const [marketData, marketHeadline] = await Promise.all([
                onLoadMarketData ? onLoadMarketData(data) : Promise.resolve(null),
                onLoadMarketHeadline ? onLoadMarketHeadline(data) : Promise.resolve(null),
            ]);
            this.marketData = marketData;
            this.marketHeadline = marketHeadline;
            this.updateMarketDataTab();
        }
        catch (error) {
            console.error('Failed to load market trends data:', error);
        }
    }
    // Current-snapshot stat strip (active/median price/$psf/absorption/
    // sale-to-list, each with a period-over-period delta) shown above the
    // trend chart - see GeographyModalOptions' onLoadMarketHeadline. Reuses
    // MarketStats' own delta()/format*() helpers (same up/down/flat logic and
    // the absorption-rate color inversion) rather than reimplementing them.
    buildMarketHeadline(h) {
        const tile = (label, value, deltaHtml) => `
      <div class="sm-geography-market-tile">
        <div class="sm-geography-market-tile-label">${label}</div>
        <div class="sm-geography-market-tile-value">${value}</div>
        ${deltaHtml}
      </div>
    `;
        return `
      <div class="sm-geography-market-headline">
        ${tile('Active', formatCount(h.activeCount), delta(h.activeCount, h.priorActiveCount, false))}
        ${tile('Median List', formatPrice(h.medianListPrice), delta(h.medianListPrice, h.priorMedianListPrice, false))}
        ${tile('Median $/sf', formatPsf(h.medianPsf), delta(h.medianPsf, h.priorMedianPsf, false))}
        ${tile('Absorption', formatRatio(h.absorptionRate), delta(h.absorptionRate, h.priorAbsorptionRate, true))}
        ${tile('Sale/List', formatPercent(h.saleToListMedian), delta(h.saleToListMedian, h.priorSaleToListMedian, false))}
      </div>
    `;
    }
    updateWithEntityData() {
        if (!this.overlay || !this.entity)
            return;
        const titleEl = this.overlay.querySelector('.sm-geography-title');
        if (titleEl) {
            titleEl.textContent = formatNeighborhoodName(this.entity.name);
        }
        const subtitleEl = this.overlay.querySelector('.sm-geography-subtitle');
        if (subtitleEl) {
            subtitleEl.textContent = this.entity.regionName || '';
        }
        void this.initEntityMap();
        this.updateContentSections();
        this.updateDisclaimers();
    }
    render() {
        this.cleanup();
        this.overlay = document.createElement('div');
        this.overlay.className = 'sm-geography-overlay';
        this.overlay.innerHTML = this.buildModalHtml();
        this.options.container.appendChild(this.overlay);
        this.attachEventListeners();
    }
    buildModalHtml() {
        const data = this.data;
        // County and Region both get the bolder "region" look (reusing that
        // CSS class rather than adding a third one) - District is the one
        // visually-lighter tier.
        const isRegion = data.entityType === 'county' || data.entityType === 'region';
        const entityLabel = geographyEntityLabel(data.entityType);
        const title = data.name || 'Loading...';
        const subtitle = data.regionName || '';
        return `
      <div class="sm-geography-modal">
        <div class="sm-geography-header">
          <div class="sm-geography-header-left">
            <p class="sm-geography-eyebrow${isRegion ? ' region' : ''}">${entityLabel}</p>
            <h2 class="sm-geography-title${isRegion ? ' region' : ''}">${this.escapeHtml(title)}</h2>
            ${subtitle ? `<p class="sm-geography-subtitle">${this.escapeHtml(subtitle)}</p>` : ''}
            ${this.buildActionButtons()}
          </div>
          <div class="sm-geography-header-right">
            <button class="sm-geography-close" aria-label="Close">&times;</button>
          </div>
        </div>
        <div class="sm-geography-tabs">
          <button class="sm-geography-tab active" data-tab="overview">Overview</button>
          <button class="sm-geography-tab" data-tab="forSale">For Sale</button>
          <button class="sm-geography-tab" data-tab="salesData">Sales Data</button>
          <button class="sm-geography-tab" data-tab="marketData">Market Data</button>
          <button class="sm-geography-tab" data-tab="latestActivity">Latest Activity</button>
          ${this.hasAgentContentTab() ? '<button class="sm-geography-tab" data-tab="content">Content</button>' : ''}
        </div>
        <div class="sm-geography-body">
          <div class="sm-geography-tab-content active" data-tab-content="overview">
            ${this.buildOverviewTab()}
          </div>
          <div class="sm-geography-tab-content" data-tab-content="forSale">
            ${this.buildForSaleTab()}
          </div>
          <div class="sm-geography-tab-content" data-tab-content="salesData">
            ${this.buildSalesTab()}
          </div>
          <div class="sm-geography-tab-content" data-tab-content="marketData">
            ${this.buildMarketDataTab()}
          </div>
          <div class="sm-geography-tab-content" data-tab-content="latestActivity">
            <div class="sm-geography-activity-container"></div>
          </div>
          ${this.hasAgentContentTab() ? `
            <div class="sm-geography-tab-content" data-tab-content="content">
              <div class="sm-geography-content-tab-container"></div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
    }
    hasAgentContentTab() {
        return !!(this.options.onLoadAgentNote && this.options.onSaveAgentNote);
    }
    // Alert bell only - see the class doc comment for why there's no
    // favorite button. Labeled with visible text (not just a hover title,
    // which mobile users never see) and amber-tinted at rest, same treatment
    // as PropertyModal/NeighborhoodModal's alert buttons.
    buildActionButtons() {
        if (!this.options.showAlertButton)
            return '';
        const data = this.data;
        const isAlerted = this.options.isAlerted?.(data.id, data.entityType) ?? false;
        const scopeLabel = geographyScopePhrase(data.entityType);
        return `
      <div class="sm-geography-actions">
        <button class="sm-geography-alert sm-geography-alert-labeled${isAlerted ? ' active' : ''}" aria-label="${isAlerted ? 'Remove alert' : 'Set alert'}" title="${isAlerted ? 'Remove alert' : `Get notified of new listings and price changes in ${scopeLabel}`}">
          <svg viewBox="0 0 24 24" fill="${isAlerted ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          <span class="sm-geography-alert-label">${isAlerted ? 'Notified' : 'Notify me'}</span>
        </button>
      </div>
    `;
    }
    handleAlertToggle() {
        const data = this.data;
        if (!data)
            return;
        // Alerts require login - checked before any visual change, not after.
        // No optimistic flip here; onAlertAuthRequired (main.ts) opens the auth
        // modal, and setAlertState() below is what actually updates this button
        // once sign-in completes and the alert is created for real.
        if (this.options.isLoggedIn && !this.options.isLoggedIn()) {
            this.options.onAlertAuthRequired?.(data.id, data.entityType);
            return;
        }
        if (!this.options.onAlertToggle)
            return;
        const btn = this.overlay?.querySelector('.sm-geography-alert');
        if (!btn)
            return;
        const isCurrentlyAlerted = btn.classList.contains('active');
        const newState = !isCurrentlyAlerted;
        this.applyAlertVisualState(btn, newState, data.entityType);
        this.options.onAlertToggle(data.id, data.entityType);
    }
    applyAlertVisualState(btn, newState, entityType) {
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-label', newState ? 'Remove alert' : 'Set alert');
        const scopeLabel = geographyScopePhrase(entityType);
        btn.setAttribute('title', newState ? 'Remove alert' : `Get notified of new listings and price changes in ${scopeLabel}`);
        const svg = btn.querySelector('svg');
        if (svg) {
            svg.setAttribute('fill', newState ? 'currentColor' : 'none');
        }
        const label = btn.querySelector('.sm-geography-alert-label');
        if (label) {
            label.textContent = newState ? 'Notified' : 'Notify me';
        }
    }
    /**
     * Updates the alert button's visual state without a click - used after a
     * pending sign-in completes and the alert was actually created for the
     * district/region this modal is currently showing. No-ops if the modal
     * has since moved on to a different entity.
     */
    setAlertState(id, entityType, isAlerted) {
        if (this.data?.id !== id || this.data?.entityType !== entityType)
            return;
        const btn = this.overlay?.querySelector('.sm-geography-alert');
        if (!btn)
            return;
        this.applyAlertVisualState(btn, isAlerted, entityType);
    }
    buildOverviewTab() {
        return `
      <div class="sm-geography-stats" id="sm-geography-stats">
        ${this.buildStatsRow()}
      </div>

      <div class="sm-geography-map" id="sm-geography-map">
        <div class="sm-geography-map-placeholder">
          <div class="sm-geography-loading-spinner"></div>
        </div>
      </div>

      <div id="sm-geography-content-sections">
        ${this.buildContentSections()}
      </div>

      <div id="sm-geography-agent-insights">
        ${this.buildAgentInsightsSection()}
      </div>

      <div id="sm-geography-disclaimers"></div>
    `;
    }
    buildStatsRow() {
        const data = this.data;
        const stats = [];
        if (data?.neighborhoodCount !== undefined) {
            stats.push({ value: data.neighborhoodCount.toLocaleString(), label: 'Neighborhoods' });
        }
        if (data?.forSaleCount !== undefined) {
            stats.push({ value: String(data.forSaleCount), label: 'For Sale' });
        }
        if (stats.length === 0) {
            return `
        <div class="sm-geography-loading">
          <div class="sm-geography-loading-spinner"></div>
          Loading...
        </div>
      `;
        }
        return stats.map(stat => `
      <div class="sm-geography-stat">
        <div class="sm-geography-stat-value">${stat.value}</div>
        <div class="sm-geography-stat-label">${stat.label}</div>
      </div>
    `).join('');
    }
    // Shows the entity's actual polygon boundary rather than a photo gallery -
    // makes it unambiguous exactly what area the stats/listings on this popup
    // are scoped to. A real google.maps.Map + Polygon overlay (not a Static
    // Maps image) since the JS Maps API is already loaded by the main map
    // this modal is opened from, and this repo has no Static Maps key/billing
    // set up separately.
    async initEntityMap() {
        const container = this.overlay?.querySelector('#sm-geography-map');
        if (!container)
            return;
        if (this.entityMapPolygon) {
            this.entityMapPolygon.setMap(null);
            this.entityMapPolygon = null;
        }
        this.entityMap = null;
        const polygon = this.entity?.polygon;
        if (!polygon || polygon.length < 3) {
            container.innerHTML = '<div class="sm-geography-map-placeholder">No boundary defined</div>';
            return;
        }
        container.innerHTML = '';
        try {
            const { Map } = await google.maps.importLibrary('maps');
            // The modal may have been closed (its overlay removed) while this
            // import was in flight - container is a direct DOM reference captured
            // before the await, so it stays "valid" (non-null) even once detached.
            // Building a full google.maps.Map in a dead container leaks the whole
            // instance forever: nothing ever calls this function's cleanup path
            // again for this already-finished close() cycle. Confirmed live via a
            // heap snapshot retainer trace (2026-10) - a stray Map + its internal
            // listener registry, anchored in a Detached <div>.
            if (!container.isConnected)
                return;
            this.entityMap = new Map(container, {
                mapTypeId: 'roadmap',
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: 'cooperative',
            });
            this.entityMapPolygon = new google.maps.Polygon({
                paths: polygon,
                strokeColor: '#2c5530',
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: '#2c5530',
                fillOpacity: 0.15,
                clickable: false,
            });
            this.entityMapPolygon.setMap(this.entityMap);
            const bounds = new google.maps.LatLngBounds();
            polygon.forEach(pt => bounds.extend(pt));
            this.entityMap.fitBounds(bounds, 24);
        }
        catch (error) {
            console.error('Failed to load boundary map:', error);
            container.innerHTML = '<div class="sm-geography-map-placeholder">Map unavailable</div>';
        }
    }
    buildContentSections() {
        const content = this.entity?.content;
        if (!content)
            return '';
        let html = '';
        for (const section of CONTENT_SECTIONS) {
            const sectionContent = content[section.id];
            if (!sectionContent)
                continue;
            const isExpanded = this.expandedContentSections.has(section.id);
            html += `
        <div class="sm-geography-section${isExpanded ? '' : ' collapsed'}" data-content-section="${section.id}">
          <button class="sm-geography-section-header">
            <span>${section.icon} ${section.name}</span>
            <span class="sm-geography-section-chevron">&#9660;</span>
          </button>
          <div class="sm-geography-section-content">
            <div class="sm-geography-content-html">${sectionContent}</div>
          </div>
        </div>
      `;
        }
        return html;
    }
    /**
     * Approved agent_notes for this district/region ("Agent Insights") -
     * Phase 3 of docs/plans/agent-content-workflow-2026-09-28.md. Distinct
     * from the "Content" tab elsewhere in this modal (a logged-in agent's
     * own private note editor) - this is public, read-only, and shows every
     * agent's approved note together. noteHtml is sanitized server-side at
     * write time (see workers/agent-api's handleAgentNotes), safe to render
     * as-is here. Same markup/classes as NeighborhoodModal's identical
     * section - shared styles.ts rules, not duplicated.
     */
    buildAgentInsightsSection() {
        const insights = this.entity?.agentInsights;
        if (!insights || insights.length === 0) {
            return '';
        }
        return `
      <div class="sm-geography-agent-insights">
        <h3 class="sm-geography-agent-insights-title">Agent Insights</h3>
        ${insights.map((insight) => `
          <div class="sm-geography-agent-insight">
            <div class="sm-geography-agent-insight-body">${insight.noteHtml}</div>
            <a class="sm-geography-agent-insight-byline" href="https://${encodeURIComponent(insight.agentSubdomain)}.slowmaui.com" target="_blank" rel="noopener noreferrer">— ${this.escapeHtml(insight.agentName)}</a>
          </div>
        `).join('')}
      </div>
    `;
    }
    updateContentSections() {
        const container = this.overlay?.querySelector('#sm-geography-content-sections');
        if (container) {
            container.innerHTML = this.buildContentSections();
            this.attachContentSectionListeners();
        }
    }
    attachContentSectionListeners() {
        const sections = this.overlay?.querySelectorAll('.sm-geography-section');
        sections?.forEach((section) => {
            const header = section.querySelector('.sm-geography-section-header');
            const sectionId = section.getAttribute('data-content-section');
            header?.addEventListener('click', () => {
                const isCollapsed = section.classList.toggle('collapsed');
                if (sectionId) {
                    if (isCollapsed) {
                        this.expandedContentSections.delete(sectionId);
                    }
                    else {
                        this.expandedContentSections.add(sectionId);
                    }
                }
            });
        });
    }
    updateDisclaimers() {
        const container = this.overlay?.querySelector('#sm-geography-disclaimers');
        if (!container)
            return;
        if (this.disclaimers)
            this.disclaimers.destroy();
        this.disclaimers = new Disclaimers({
            container,
            showIdx: false,
            showNeighborhood: true,
        });
        this.disclaimers.render();
    }
    buildForSaleTab() {
        if (this.listings.length === 0) {
            return `
        <div class="sm-geography-loading" id="sm-geography-listings-loading">
          <div class="sm-geography-loading-spinner"></div>
          Loading listings...
        </div>
        <div class="sm-geography-listings" id="sm-geography-listings" style="display: none;"></div>
      `;
        }
        return `<div class="sm-geography-listings" id="sm-geography-listings">${this.buildListingsContent()}</div>`;
    }
    buildListingsContent() {
        if (this.listings.length === 0) {
            return '<div class="sm-geography-listing-empty">No active listings</div>';
        }
        return this.listings.map(listing => this.buildListingCard(listing)).join('');
    }
    buildListingCard(listing) {
        const price = this.formatPrice(listing.listPrice);
        const photo = listing.photoUrl || listing.primaryPhotoUrl;
        const imageHtml = photo
            ? `<img class="sm-geography-listing-image" src="${this.escapeHtml(photo)}" alt="Property photo" loading="lazy">`
            : `<div class="sm-geography-listing-image-placeholder"></div>`;
        // Mirrors NeighborhoodModal's buildListingCard - see its comment for why
        // this tab shows Active+Pending together and needs the badge.
        const pendingBadge = listing.status === 'Pending'
            ? `<span class="sm-geography-listing-status-badge pending">Pending</span>`
            : '';
        return `
      <div class="sm-geography-listing-card" data-listing-id="${listing.id}">
        ${imageHtml}
        <div class="sm-geography-listing-info">
          <div class="sm-geography-listing-price">${price}${pendingBadge}</div>
          <div class="sm-geography-listing-address">${this.escapeHtml(formatListingAddress(listing))}</div>
          <div class="sm-geography-listing-stats">
            ${listing.bedrooms ? `<span>${listing.bedrooms} bd</span>` : ''}
            ${listing.bathrooms ? `<span>${listing.bathrooms} ba</span>` : ''}
            ${listing.livingArea ? `<span>${listing.livingArea.toLocaleString()} sqft</span>` : ''}
          </div>
        </div>
      </div>
    `;
    }
    updateListingsTab() {
        const loading = this.overlay?.querySelector('#sm-geography-listings-loading');
        const container = this.overlay?.querySelector('#sm-geography-listings');
        if (loading)
            loading.remove();
        if (container) {
            container.style.display = '';
            container.innerHTML = this.buildListingsContent();
            this.attachListingListeners();
        }
    }
    buildSalesTab() {
        if (!this.salesData) {
            return `
        <div class="sm-geography-loading" id="sm-geography-sales-loading">
          <div class="sm-geography-loading-spinner"></div>
          Loading sales data...
        </div>
        <div id="sm-geography-sales-content" style="display: none;"></div>
      `;
        }
        return `<div id="sm-geography-sales-content">${this.buildSalesContent()}</div>`;
    }
    buildSalesContent() {
        if (!this.salesData) {
            return '<div class="sm-geography-sales-empty">No sales data available</div>';
        }
        if (this.salesData.chartData) {
            return `
        <div id="sm-geography-sales-chart-container"></div>
        <div id="sm-geography-sales-disclaimers"></div>
      `;
        }
        const data = this.salesData;
        const stats = [];
        if (data.salesCount1yr !== undefined) {
            stats.push({ value: String(data.salesCount1yr), label: 'Sales (1 yr)' });
        }
        if (data.avgSalePrice !== undefined) {
            stats.push({ value: this.formatPrice(data.avgSalePrice), label: 'Avg Sale Price' });
        }
        if (data.avgDaysOnMarket !== undefined) {
            stats.push({ value: String(data.avgDaysOnMarket), label: 'Avg Days on Market' });
        }
        if (data.pricePerSqft !== undefined) {
            stats.push({ value: `$${data.pricePerSqft.toLocaleString()}`, label: 'Price/Sq Ft' });
        }
        if (stats.length === 0) {
            return '<div class="sm-geography-sales-empty">No sales data available</div>';
        }
        return `
      <div class="sm-geography-sales-stats">
        ${stats.map(stat => `
          <div class="sm-geography-sales-stat">
            <div class="sm-geography-sales-stat-value">${stat.value}</div>
            <div class="sm-geography-sales-stat-label">${stat.label}</div>
          </div>
        `).join('')}
      </div>
      <div id="sm-geography-sales-disclaimers"></div>
    `;
    }
    updateSalesTab() {
        const loading = this.overlay?.querySelector('#sm-geography-sales-loading');
        const container = this.overlay?.querySelector('#sm-geography-sales-content');
        if (loading)
            loading.remove();
        if (container) {
            container.style.display = '';
            container.innerHTML = this.buildSalesContent();
            if (this.salesData?.chartData) {
                const chartContainer = container.querySelector('#sm-geography-sales-chart-container');
                if (chartContainer) {
                    if (this.salesChart)
                        this.salesChart.destroy();
                    this.salesChart = new SalesChart({
                        container: chartContainer,
                        data: this.salesData.chartData,
                        onDataPointClick: (point) => {
                            console.log('Sales chart point clicked:', point);
                        },
                    });
                }
            }
            // See NeighborhoodModal's matching comment - aggregate-data
            // disclaimer replaces idx as the always-visible one here, mls/
            // general/ai stay behind the toggle.
            const disclaimersContainer = container.querySelector('#sm-geography-sales-disclaimers');
            if (disclaimersContainer) {
                this.salesDisclaimers?.destroy();
                this.salesDisclaimers = new Disclaimers({
                    container: disclaimersContainer,
                    showIdx: false,
                    showAggregate: true,
                });
                this.salesDisclaimers.render();
            }
        }
    }
    buildMarketDataTab() {
        if (!this.marketData) {
            return `
        <div class="sm-geography-loading" id="sm-geography-market-loading">
          <div class="sm-geography-loading-spinner"></div>
          Loading market data...
        </div>
        <div id="sm-geography-market-content" style="display: none;"></div>
      `;
        }
        return `<div id="sm-geography-market-content"><div id="sm-geography-market-chart-container"></div></div>`;
    }
    updateMarketDataTab() {
        const loading = this.overlay?.querySelector('#sm-geography-market-loading');
        const container = this.overlay?.querySelector('#sm-geography-market-content');
        if (loading)
            loading.remove();
        if (container) {
            container.style.display = '';
            container.innerHTML = `
        ${this.marketHeadline ? this.buildMarketHeadline(this.marketHeadline) : ''}
        <div id="sm-geography-market-chart-container"></div>
      `;
            const chartContainer = container.querySelector('#sm-geography-market-chart-container');
            if (chartContainer && this.marketData) {
                if (this.marketChart)
                    this.marketChart.destroy();
                this.marketChart = new MarketTrendsChart({
                    container: chartContainer,
                    data: this.marketData,
                });
            }
        }
    }
    attachEventListeners() {
        if (!this.overlay)
            return;
        const closeBtn = this.overlay.querySelector('.sm-geography-close');
        closeBtn?.addEventListener('click', () => this.close());
        const alertBtn = this.overlay.querySelector('.sm-geography-alert');
        alertBtn?.addEventListener('click', () => this.handleAlertToggle());
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay)
                this.close();
        });
        document.addEventListener('keydown', this.handleKeydown);
        const tabs = this.overlay.querySelectorAll('.sm-geography-tab');
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const tabId = tab.dataset.tab;
                this.switchTab(tabId);
            });
        });
    }
    attachListingListeners() {
        const cards = this.overlay?.querySelectorAll('.sm-geography-listing-card');
        cards?.forEach(card => {
            card.addEventListener('click', () => {
                const listingId = card.dataset.listingId;
                const listing = this.listings.find(l => l.id === listingId);
                if (listing && this.options.onListingClick) {
                    this.options.onListingClick(listing);
                }
            });
        });
    }
    switchTab(tabId) {
        if (this.currentTab === tabId)
            return;
        this.currentTab = tabId;
        const tabs = this.overlay?.querySelectorAll('.sm-geography-tab');
        tabs?.forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === tabId);
        });
        const contents = this.overlay?.querySelectorAll('.sm-geography-tab-content');
        contents?.forEach(content => {
            content.classList.toggle('active', content.dataset.tabContent === tabId);
        });
        if (tabId === 'forSale' && this.listings.length === 0 && this.data) {
            this.loadListings(this.data);
        }
        else if (tabId === 'salesData' && !this.salesData && this.data) {
            this.loadSalesData(this.data);
        }
        else if (tabId === 'marketData' && !this.marketData && this.data) {
            this.loadMarketData(this.data);
        }
        else if (tabId === 'latestActivity' && this.data) {
            this.loadActivityTab(this.data);
        }
        else if (tabId === 'content' && !this.agentNoteLoaded && this.data) {
            void this.loadContentTab(this.data);
        }
    }
    /**
     * Content tab (Agent Insight) - loads the agent's existing note (if any)
     * on first switch only. Re-switching to this tab after that does NOT
     * re-render, so an unsaved in-progress edit in the textarea survives
     * navigating to another tab and back.
     */
    async loadContentTab(data) {
        const container = this.overlay?.querySelector('.sm-geography-content-tab-container');
        if (!container || !this.options.onLoadAgentNote)
            return;
        container.innerHTML = `<div class="sm-geography-loading"><div class="sm-geography-loading-spinner"></div>Loading...</div>`;
        try {
            const note = await this.options.onLoadAgentNote(data);
            this.agentNoteId = note?.id ?? null;
            this.agentNoteText = note?.noteHtml ?? '';
            this.agentNoteStatus = note?.status ?? null;
            this.agentNoteAdminNote = note?.adminNote ?? null;
        }
        catch (error) {
            console.error('Failed to load agent note:', error);
        }
        this.agentNoteLoaded = true;
        const scopeLabel = geographyEntityLabel(data.entityType).toLowerCase();
        container.innerHTML = `
      <p class="sm-geography-content-tab-hint">Add your own insight about this ${scopeLabel} - saved under your agent profile.</p>
      <div class="sm-geography-note-status-row">${this.renderAgentNoteStatusBadge()}</div>
      <textarea class="sm-geography-content-textarea" placeholder="Share what makes this ${scopeLabel} special...">${this.escapeHtml(this.agentNoteText)}</textarea>
      <div class="sm-geography-content-tab-actions">
        <span class="sm-geography-content-tab-status"></span>
        <button class="sm-geography-content-save-btn" type="button">Save</button>
      </div>
    `;
        this.attachContentTabListeners(data);
    }
    /**
     * Status pill for the Content tab, reflecting the agent_notes moderation
     * state (migration 050) - pending until an admin reviews it in the Review
     * Queue, approved once it's live in Agent Insights, or rejected (with the
     * admin's reason, if given). No badge for a note that's never been saved.
     */
    renderAgentNoteStatusBadge() {
        if (!this.agentNoteId || !this.agentNoteStatus)
            return '';
        const labels = {
            pending: 'Pending review',
            approved: 'Live',
            rejected: 'Rejected',
        };
        const badge = `<span class="sm-geography-note-badge sm-geography-note-badge-${this.agentNoteStatus}">${labels[this.agentNoteStatus]}</span>`;
        if (this.agentNoteStatus === 'rejected' && this.agentNoteAdminNote) {
            return `${badge} <span class="sm-geography-note-rejection-reason">${this.escapeHtml(this.agentNoteAdminNote)}</span>`;
        }
        return badge;
    }
    attachContentTabListeners(data) {
        const container = this.overlay?.querySelector('.sm-geography-content-tab-container');
        const saveBtn = container?.querySelector('.sm-geography-content-save-btn');
        const textarea = container?.querySelector('.sm-geography-content-textarea');
        const status = container?.querySelector('.sm-geography-content-tab-status');
        if (!saveBtn || !textarea)
            return;
        saveBtn.addEventListener('click', async () => {
            if (!this.options.onSaveAgentNote)
                return;
            const noteHtml = textarea.value.trim();
            if (!noteHtml)
                return;
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';
            try {
                const result = await this.options.onSaveAgentNote(data, noteHtml, this.agentNoteId);
                this.agentNoteId = result.id;
                this.agentNoteText = noteHtml;
                // Every save (create or edit) resets the note to 'pending' server-side
                // (see workers/agent-api's handleAgentNotes) - mirror that here so the
                // badge doesn't keep showing a stale 'approved'/'rejected' state.
                this.agentNoteStatus = 'pending';
                this.agentNoteAdminNote = null;
                const statusRow = container?.querySelector('.sm-geography-note-status-row');
                if (statusRow)
                    statusRow.innerHTML = this.renderAgentNoteStatusBadge();
                if (status) {
                    status.textContent = 'Saved';
                    setTimeout(() => { if (status)
                        status.textContent = ''; }, 2000);
                }
            }
            catch (error) {
                console.error('Failed to save agent note:', error);
                if (status)
                    status.textContent = 'Failed to save';
            }
            finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        });
    }
    loadActivityTab(data) {
        if (!this.options.onLoadActivity)
            return;
        const container = this.overlay?.querySelector('.sm-geography-activity-container');
        if (!container)
            return;
        if (!this.activityTable) {
            this.activityTable = new ActivityTable({
                container,
                showNeighborhoodColumn: true,
                onLoadActivity: (days) => this.options.onLoadActivity(data, days),
                ...(this.options.onActivityListingClick ? { onListingClick: this.options.onActivityListingClick } : {}),
            });
        }
        void this.activityTable.load();
    }
    handleKeydown = (e) => {
        if (e.key === 'Escape' && this.overlay?.classList.contains('active')) {
            this.close();
        }
    };
    show() {
        if (this.overlay) {
            void this.overlay.offsetHeight;
            this.overlay.classList.add('active');
            document.body.style.overflow = 'hidden';
        }
    }
    close() {
        if (!this.overlay)
            return;
        this.overlay.classList.remove('active');
        document.body.style.overflow = '';
        // Snapshot the instance being closed, then immediately detach `this`'s
        // fields from it (rather than 300ms from now) so a rapid reopen during
        // the fade-out - open() runs synchronously, so it can land well inside
        // this window on a quick double-tap - builds its own fresh overlay/map
        // instead of racing this timer for `this.overlay`/`this.entityMap`.
        // Without this, the deferred cleanup() below would tear down whichever
        // instance `this` happens to point to when it fires (the NEW one, if
        // reopened fast enough), while the actual closed instance - its overlay
        // element and its live google.maps.Map - is never removed from the DOM
        // and leaks for the rest of the session.
        const closed = this.snapshotResources();
        this.resetResourceFields();
        setTimeout(() => {
            this.cleanup(closed);
        }, 300);
        this.options.onClose?.();
    }
    snapshotResources() {
        return {
            overlay: this.overlay,
            entityMap: this.entityMap,
            entityMapPolygon: this.entityMapPolygon,
            salesChart: this.salesChart,
            marketChart: this.marketChart,
            disclaimers: this.disclaimers,
            salesDisclaimers: this.salesDisclaimers,
        };
    }
    resetResourceFields() {
        this.overlay = null;
        this.salesChart = null;
        this.marketChart = null;
        this.disclaimers = null;
        this.salesDisclaimers = null;
        this.entityMap = null;
        this.entityMapPolygon = null;
        this.expandedContentSections = new Set(['overview']);
        this.agentNoteId = null;
        this.agentNoteText = '';
        this.agentNoteStatus = null;
        this.agentNoteAdminNote = null;
        this.agentNoteLoaded = false;
    }
    /** Tears down a specific snapshot of resources - never `this.*` directly,
     * so it's safe to run after a rapid reopen has already moved `this`'s
     * fields on to a newer instance (see close()). */
    cleanup(resources = this.snapshotResources()) {
        resources.salesChart?.destroy();
        resources.marketChart?.destroy();
        resources.disclaimers?.destroy();
        resources.salesDisclaimers?.destroy();
        resources.entityMapPolygon?.setMap(null);
        resources.overlay?.remove();
        if (resources.overlay === this.overlay) {
            this.resetResourceFields();
        }
    }
    destroy() {
        document.removeEventListener('keydown', this.handleKeydown);
        this.cleanup();
        this.data = null;
        this.entity = null;
        this.listings = [];
        this.salesData = null;
        this.marketData = null;
        this.marketHeadline = null;
    }
    formatPrice(amount) {
        if (amount >= 1000000) {
            return `$${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)}M`;
        }
        return `$${Math.round(amount / 1000)}K`;
    }
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}
//# sourceMappingURL=GeographyModal.js.map